/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormGroup, FormText, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { resetAllTenantConfig, resetAllTenantDefect, resetAllTenantFacilityLocation, resetAllTenantFieldOption, updateTenant } from "../../../actions/Tenants/actions";
import useConfig, { useEDDateFormats } from "../../../actions/Tenants/config/configHook";
import { AvosAction } from "../../../actions/types";
import { toast } from "../../../utils/toast";
import { Button } from "../../Helper/Button";
import { formatDateForFilename } from "../Layers/fields/formatters";

const exportData = (data, fileName, type) => {
    // Create a link and download the file
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
};

export default function TenantImportExportModal({ importModal, setImportModal }) {
    const [importData, setImportData] = useState<any>();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const isLoading = useSelector((state: any) => state.tenants.isLoading);
    const isUpdating = useSelector((state: any) => state.tenants.isUpdating);
    const tenant = useSelector((state: any) => state.tenants.current);
    const options_by_field = useSelector((state: any) => state?.tenants?.options_by_field);
    const defects_by_fruit_type = useSelector((state: any) => state?.tenants?.defects_by_fruit_type);
    const facility_locations = useSelector((state: any) => state?.tenants?.locations);
    // const configs = useSelector((state) => state?.tenants?.configs);
    const dispatch = useDispatch();
    const config = useConfig();
    const edFormats = useEDDateFormats();

    const [fileSelectorValue, setFileSelectorValue] = useState("");
    const fileChangeHandler = (e) => {
        const file = e.target.files[0];

        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const jsonData = JSON.parse(event?.target?.result as string || "{}");
                    setImportData(jsonData);
                    setSelectedFile(file);
                } catch (error) {
                    toast.error(`Error parsing JSON file: ${error}`, {
                        autoClose: 1500,
                    });
                }
            };
            setFileSelectorValue(e.target.value);
            reader.readAsText(file);
        }
    };

    const exportOptions = () => {
        const form_options = options_by_field[tenant.tenant_id];
        const defects = defects_by_fruit_type[tenant.tenant_id];
        const { configs } = config;

        const data = {
            tenant,
            form_options: Object.keys(form_options).flatMap((field) => form_options[field]),
            configs,
            defects: Object.keys(defects).flatMap((fruit_type) => defects[fruit_type]),
            facility_locations
        };

        const jsonData = JSON.stringify(data, null, 2);

        exportData(jsonData, `export-${tenant.tenant_name}-${formatDateForFilename(edFormats.datetime_format)}-all.json`, "application/json;charset=utf-8;");
    };

    const handleImport = () => {
        if (!importData) return;

        // An export of another tenant is a template: import it into the tenant being edited, which keeps its own name
        const { tenant_id, tenant_name } = tenant;
        const promises = [
            dispatch(updateTenant({ ...importData.tenant, tenant_id, tenant_name }) as any),
            dispatch(resetAllTenantFieldOption(tenant_id, importData.form_options) as AvosAction)
        ];

        if (importData.configs) {
            promises.push(dispatch(resetAllTenantConfig(tenant_id, importData.configs) as any));
        }

        if (importData.defects) {
            promises.push(dispatch(resetAllTenantDefect(tenant_id, importData.defects) as AvosAction));
        }

        if (importData.facility_locations) {
            promises.push(dispatch(resetAllTenantFacilityLocation(tenant_id, importData.facility_locations) as AvosAction));
        }

        Promise.all(promises).then(() => {
            setImportModal(false);
        });

    };

    return <Modal size="lg" isOpen={importModal} toggle={() => setImportModal(false)} >
        <ModalHeader toggle={() => setImportModal(false)} >Import / Delete all</ModalHeader>
        <ModalBody>
            <p>This will either import or export all tenant settings, including power bi settings, fields, form fields, form options and legacy settings. An import always goes into this tenant and keeps its name and ID. <b>An import will remove all current fields and forms!</b> Use with care.</p>
            <FormGroup>
                <Label for="exampleFile">JSON</Label>
                <Input
                    id="exampleFile"
                    name="file"
                    type="file"
                    value={fileSelectorValue}
                    onChange={fileChangeHandler}
                />

                {selectedFile ? (
                    <div className="py-3">
                        <FormText >
                            <div>Filename: {selectedFile.name}</div>
                            <div>Filetype: {selectedFile.type}</div>
                            <div>Size in bytes: {selectedFile.size}</div>
                            <div>Configs: <b>{importData && importData?.configs?.length}</b></div>
                            <div>Form Options: <b>{importData && importData?.form_options?.length}</b></div>
                            <div>Defects: <b>{importData && importData?.defects?.length}</b></div>
                            <div>Facility locations: <b>{importData && importData?.facility_locations?.length}</b></div>
                        </FormText>
                    </div>

                ) : (<FormText>Upload json file here.</FormText>)}
            </FormGroup>
            {importData && importData.tenant.tenant_id !== tenant.tenant_id && <div className="alert alert-warning">This file is an export of {importData.tenant.tenant_name} ({importData.tenant.tenant_id}).
                Its settings will replace those of {tenant.tenant_name} ({tenant.tenant_id}); {importData.tenant.tenant_name} itself is not changed and {tenant.tenant_name} keeps its name and ID.</div>}
            {importData && <div css={css`max-height: 70vh;overflow-y:scroll;`}>
                <pre>
                    {JSON.stringify(importData, null, 2)}
                </pre>
            </div>
            }

        </ModalBody>
        <ModalFooter>
            <Button color="light" onClick={() => setImportModal(false)}>Cancel</Button>
            <Button color="light" className="me-1 mb-1" onClick={exportOptions}>Export</Button>
            <Button color="primary" disabled={isLoading || isUpdating || !importData} className="me-1 mb-1" onClick={handleImport}>Import</Button>
        </ModalFooter>
    </Modal>;
}

TenantImportExportModal.propTypes = {
    importModal: PropTypes.bool.isRequired,
    setImportModal: PropTypes.func.isRequired
};
